import React, { useState, useEffect } from 'react';
import { Card, Collapse, IconButton, Grid, TableCell, TableRow } from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import StageExpandableTable from '../tabels/StageExpandableTable';
import Icon from '@mui/material/Icon';
import { grey } from '@mui/material/colors';
import PropTypes from 'prop-types';
import ProductList from './ProductList';
import DiffRenderer from './DiffRenderer';

function PricingModelReview({ selectedOrg, productData, showSameFields }) {
  const [expandedRow, setExpandedRow] = useState(null);
  const [productDataMap, setProductDataMap] = useState({});
  const [selectedProductId, setSelectedProductId] = useState(null);

  const columns = [
    { name: 'item', align: 'left' },
    { name: 'actions', align: 'right', width: '50px' },
  ];

  useEffect(() => {
    if (!productData || productData.length === 0 || !selectedOrg) {
      setProductDataMap({});
      return;
    }

    const generatedProductDataMap = {};
    const isRevenue = selectedOrg.type === 'RC';

    // Generic helper to process arrays of diffs
    const processDiffArray = (diffArray, sObjectName, nameField = 'name') => {
      const result = { new: [], changed: [], deleted: [], same: [] };
      ['new', 'changed', 'deleted', 'same'].forEach((type) => {
        (diffArray?.[type] || []).forEach((item) => {
          // Handle potential wrappers like { priceDimensions: {...} }
          const itemData = item[sObjectName] || item;
          result[type].push({
            id: itemData.sfId || itemData.id || itemData.recordId,
            item: (
              <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
                {itemData[nameField] || itemData.name || `Item - ${itemData.sfId || itemData.recordId}`}
              </ArgonTypography>
            ),
            diffs: item.changes,
            sObjectName: sObjectName,
            type: type,
          });
        });
      });
      return result;
    };

    productData.forEach((productDiff) => {
      const productId = productDiff.product.sfId || productDiff.product.id;

      if (isRevenue) {
        // --- RC DATA PROCESSING ---
        const pricing = productDiff.pricing || {};

        generatedProductDataMap[productId] = {
          productName: productDiff.product.name,
          sections: [
            {
              title: 'Attribute Based Adjustments',
              data: processDiffArray(
                pricing.attributeBasedAdjustments,
                'AttributeBasedAdjustment',
                'attributeBasedAdjRuleName'
              ),
            },
            {
              title: 'Bundle Based Adjustments',
              data: processDiffArray(
                pricing.bundleBasedAdjustments,
                'BundleBasedAdjustment',
                'priceAdjustmentScheduleName'
              ),
            },
            {
              title: 'Cost Book Entries',
              data: processDiffArray(pricing.costBookEntries, 'CostBookEntry', 'costBookName'),
            },
            {
              title: 'Price Adjustment Tiers',
              data: processDiffArray(
                pricing.priceAdjustmentTiers,
                'PriceAdjustmentTier',
                'priceAdjustmentScheduleName'
              ),
            },
          ],
        };
      } else {
        // --- CPQ DATA PROCESSING ---
        const pricingModel = productDiff.pricingModel || {};
        generatedProductDataMap[productId] = {
          productName: productDiff.product.name,
          sections: [
            {
              title: 'Price Dimensions',
              data: processDiffArray(pricingModel.priceDimensions, 'SBQQ__PriceDimension__c'),
            },
            { title: 'Block Prices', data: processDiffArray(pricingModel.blockPrices, 'SBQQ__BlockPrice__c') },
            { title: 'Costs', data: processDiffArray(pricingModel.costs, 'SBQQ__Cost__c') },
          ],
        };
      }
    });

    setProductDataMap(generatedProductDataMap);
    if (productData.length > 0) {
      setSelectedProductId(productData[0].product.sfId || productData[0].product.id);
    }
  }, [productData, selectedOrg]);

  const handleRowClick = (rowId) => {
    setExpandedRow((prev) => (prev === rowId ? null : rowId));
  };

  const handleProductSelect = (productId) => {
    setSelectedProductId(productId);
    setExpandedRow(null); // Reset expansion when product changes
  };

  const renderExpandedContent = (row) => (
    <TableRow>
      <TableCell colSpan={columns.length} style={{ padding: '0px' }}>
        <Collapse in={expandedRow === row.id} timeout="auto" unmountOnExit>
          <ArgonBox p={3} bgcolor={grey[50]}>
            <DiffRenderer
              changes={row.diffs}
              sObjectName={row.sObjectName}
              orgType={selectedOrg.type.toLowerCase()}
              showSameFields={showSameFields}
            />
          </ArgonBox>
        </Collapse>
      </TableCell>
    </TableRow>
  );

  const renderProductSection = (productName, productData) => {
    const { sections } = productData;

    const renderTableSection = (title, rows) => {
      if (!rows || rows.length === 0) return null;

      const formattedRows = rows.map((row) => ({
        ...row,
        onClick: () => handleRowClick(row.id),
        selected: expandedRow === row.id,
        actions: (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick(row.id);
            }}
          >
            <Icon>{expandedRow === row.id ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
          </IconButton>
        ),
      }));

      return (
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                {title}
              </ArgonTypography>
              <StageExpandableTable
                rows={formattedRows}
                columns={columns}
                renderRowSubComponent={({ row }) => renderExpandedContent(row)}
              />
            </ArgonBox>
          </Card>
        </ArgonBox>
      );
    };

    const hasAnyData = sections.some((section) =>
      ['new', 'changed', 'deleted', 'same'].some((type) => section.data[type] && section.data[type].length > 0)
    );

    if (!hasAnyData) {
      return (
        <Card>
          <ArgonBox p={3} textAlign="center">
            <ArgonTypography variant="body2" color="textSecondary">
              No pricing data available for this product.
            </ArgonTypography>
          </ArgonBox>
        </Card>
      );
    }

    return (
      <ArgonBox key={productName}>
        {sections.map((section) => (
          <React.Fragment key={section.title}>
            {['new', 'changed', 'deleted', 'same'].map(
              (type) =>
                section.data[type] &&
                section.data[type].length > 0 &&
                renderTableSection(
                  `${type.charAt(0).toUpperCase() + type.slice(1)} ${section.title}`,
                  section.data[type]
                )
            )}
          </React.Fragment>
        ))}
      </ArgonBox>
    );
  };

  const productList = Object.keys(productDataMap).map((productId) => ({
    id: productId,
    name: productDataMap[productId].productName,
  }));

  return (
    <ArgonBox>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <ProductList
            products={productList}
            selectedProductId={selectedProductId}
            onProductSelect={handleProductSelect}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          {selectedProductId && productDataMap[selectedProductId] ? (
            renderProductSection(productDataMap[selectedProductId].productName, productDataMap[selectedProductId])
          ) : (
            <Card>
              <ArgonBox p={3} textAlign="center">
                <ArgonTypography variant="body2" color="textSecondary">
                  Select a product to view its pricing data.
                </ArgonTypography>
              </ArgonBox>
            </Card>
          )}
        </Grid>
      </Grid>
    </ArgonBox>
  );
}

PricingModelReview.propTypes = {
  productData: PropTypes.array.isRequired,
  showSameFields: PropTypes.bool,
  selectedOrg: PropTypes.object.isRequired,
};

export default PricingModelReview;
